import React, { useState, useEffect } from 'react';
import { Award, ToggleLeft, ToggleRight, AlertCircle, Printer, ChevronDown } from 'lucide-react';
import { ReportCard } from '../student/ReportCardPage';
import api from '../../api';

export default function ParentResultsPage() {
  const [children, setChildren] = useState([]);
  const [selectedChild, setSelectedChild] = useState(null);
  const [childTerms, setChildTerms] = useState([]);   // flat list of terms for selected child's school
  const [selectedTermId, setSelectedTermId] = useState('');
  const [cumulative, setCumulative] = useState(false);
  const [reportCard, setReportCard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [childrenLoading, setChildrenLoading] = useState(true);

  useEffect(() => {
    api.get('/exams/parent/children')
      .then(res => {
        if (res?.success && res.data?.length > 0) {
          setChildren(res.data);
          const first = res.data[0];
          setSelectedChild(first);
          const terms = first.school_terms || [];
          setChildTerms(terms);
          const current = terms.find(t => t.is_current) || terms[terms.length - 1];
          if (current) {
            setSelectedTermId(current.id);
            setCumulative((current.name || '').toLowerCase().includes('third'));
          }
        }
      })
      .finally(() => setChildrenLoading(false));
  }, []);

  // When child changes, switch term list to that child's school
  const handleChildChange = (childId) => {
    const child = children.find(c => c.id === childId);
    if (!child) return;
    setSelectedChild(child);
    setReportCard(null);
    const terms = child.school_terms || [];
    setChildTerms(terms);
    const current = terms.find(t => t.is_current) || terms[terms.length - 1];
    if (current) {
      setSelectedTermId(current.id);
      setCumulative((current.name || '').toLowerCase().includes('third'));
    }
  };

  useEffect(() => {
    if (!selectedChild || !selectedTermId) return;
    setLoading(true);
    setError('');
    api.get(`/exams/parent/child-report-card/${selectedChild.id}?term_id=${selectedTermId}&cumulative=${cumulative}`)
      .then(d => { if (d.success) setReportCard(d); })
      .catch(err => setError(err?.detail || 'Failed to load results'))
      .finally(() => setLoading(false));
  }, [selectedChild, selectedTermId, cumulative]);

  const handleTermChange = (e) => {
    const tid = e.target.value;
    setSelectedTermId(tid);
    const t = childTerms.find(t => t.id === tid);
    setCumulative((t?.name || '').toLowerCase().includes('third'));
  };

  if (childrenLoading) return (
    <div className="flex items-center justify-center py-20">
      <div className="w-8 h-8 border-4 border-[#2563eb] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="p-4 lg:p-6 max-w-4xl mx-auto" data-testid="parent-results-page">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5 no-print">
        <div>
          <h1 className="text-2xl font-black text-slate-900">
            {children.length === 1 ? `${children[0].first_name}'s Report Card` : "Children's Results"}
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">Academic report for your child</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {children.length > 1 && (
            <div className="relative">
              <select value={selectedChild?.id || ''} onChange={e => handleChildChange(e.target.value)}
                className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white outline-none appearance-none pr-8"
                data-testid="child-select">
                {children.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.first_name} {c.last_name}{c.school_name ? ` — ${c.school_name}` : ''}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
            </div>
          )}
          {childTerms.length > 0 && (
            <div className="relative">
              <select value={selectedTermId} onChange={handleTermChange}
                className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white outline-none appearance-none pr-8"
                data-testid="parent-term-select">
                {childTerms.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
              <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
            </div>
          )}
          <button onClick={() => setCumulative(c => !c)}
            className={`flex items-center gap-1.5 text-sm px-3 py-2 rounded-xl border transition-colors ${
              cumulative ? 'bg-[#2563eb] text-white border-[#2563eb]' : 'bg-white text-slate-600 border-slate-200'
            }`} data-testid="parent-cumulative-toggle">
            {cumulative ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
            Cumulative
          </button>
          <button onClick={() => window.print()}
            className="flex items-center gap-1.5 bg-[#2563eb] text-white text-sm px-4 py-2 rounded-xl hover:bg-[#1d58d4] transition-colors font-bold"
            data-testid="parent-print-btn">
            <Printer className="w-3.5 h-3.5" /> Print
          </button>
        </div>
      </div>

      {children.length === 0 ? (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-8 text-center no-print">
          <AlertCircle className="w-10 h-10 text-amber-400 mx-auto mb-3" />
          <p className="font-medium text-amber-800">No linked children found</p>
          <p className="text-sm text-amber-700 mt-1">Please contact the school admin to link your account</p>
        </div>
      ) : loading ? (
        <div className="flex justify-center py-16 no-print">
          <div className="w-7 h-7 border-4 border-[#2563eb] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : error ? (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 flex items-start gap-3 no-print">
          <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0" />
          <p className="text-sm text-amber-700">{error}</p>
        </div>
      ) : reportCard ? (
        <ReportCard data={reportCard} />
      ) : null}

      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: white !important; }
          .report-card-wrapper { border: none !important; box-shadow: none !important; border-radius: 0 !important; }
        }
      `}</style>
    </div>
  );
}
